// Witte AppBar met tabs (Today/Plan/Organize/Dashboard), logo, avatar.
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks';
import { useT } from '@/i18n/useT';
import { HIGHLIGHT_PROJECT_FILTER_EVENT } from '@/components/layout/filterBarUtils';
import {
  isBattlefieldBreakpoint,
  isDesktopBreakpoint,
  useBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { getInboxTabCount } from '@/features/organize/utils/inboxTriageUtils';
import {
  getVisiblePages,
  getTabIndex,
  getUserInitials,
} from '@/components/layout/appBarHeaderNav';
import { renderContentFirstAppBar } from '@/components/layout/appBarHeaderContentFirst';
import { renderDefaultAppBar } from '@/components/layout/appBarHeaderDefault';
import {
  renderAppBarFilterDrawer,
} from '@/components/layout/appBarHeaderShared';
import HelpPanelDialog from '@/components/layout/HelpPanelDialog';
import SettingsDialog from '@/components/layout/SettingsDialog';
import { isOnboardingWelcomePath } from '@/constants/onboardingRoutes';

export default function AppBarHeader({ contentFirstTabOrder = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const { user } = useAuth();
  const { allTasks, projectFilter, contextFilter } = useTasksContext();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const isDesktop = isDesktopBreakpoint(currentBreakpoint);
  const isWelcomeRoute = isOnboardingWelcomePath(location.pathname);
  const visiblePages = useMemo(() => getVisiblePages(isDesktop), [isDesktop]);

  const tab = getTabIndex(location.pathname, visiblePages);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const accountMenuTriggerRef = useRef(null);

  useEffect(() => {
    if (!isBattlefield) return undefined;
    const onHighlight = () => setFilterDrawerOpen(true);
    window.addEventListener(HIGHLIGHT_PROJECT_FILTER_EVENT, onHighlight);
    return () => window.removeEventListener(HIGHLIGHT_PROJECT_FILTER_EVENT, onHighlight);
  }, [isBattlefield]);

  const inboxCount = useMemo(() => getInboxTabCount(allTasks), [allTasks]);
  const hasFilter = projectFilter.length > 0 || contextFilter.length > 0;
  const displayName = user?.displayName || user?.email || '';
  const initials = getUserInitials(displayName);
  const onTabChange = useCallback(
    (_, v) => navigate(visiblePages[v].path),
    [navigate, visiblePages],
  );
  const onLogout = useCallback(() => {
    setAvatarAnchor(null);
    navigate('/app/logout');
  }, [navigate]);
  const onOpenHelp = useCallback(() => {
    setAvatarAnchor(null);
    setHelpOpen(true);
  }, []);
  const onOpenSettings = useCallback(() => {
    setAvatarAnchor(null);
    setSettingsOpen(true);
  }, []);

  const sharedBarProps = {
    isDesktop,
    tab,
    visiblePages,
    onTabChange,
    t,
    logoAlt: t('appbar.logoAlt'),
    initials,
    avatarAnchor,
    setAvatarAnchor,
    onLogout,
    onOpenHelp,
    onOpenSettings,
    accountMenuTriggerRef,
    helpLabel: t('help.menuItem'),
    hideQuickFilters: isWelcomeRoute,
  };

  return (
    <Box
      id="app-chrome"
      sx={{
        flexShrink: 0,
        zIndex: 1100,
        ...(contentFirstTabOrder && !isBattlefield ? { order: 1 } : {}),
      }}
    >
      {contentFirstTabOrder && !isBattlefield
        ? renderContentFirstAppBar(sharedBarProps)
        : renderDefaultAppBar({
            ...sharedBarProps,
            isBattlefield,
            hasFilter,
            inboxCount,
            onOpenFilter: () => setFilterDrawerOpen(true),
            onOpenInbox: () => navigate('/app/inbox'),
          })}

      {isBattlefield && !isWelcomeRoute
        ? renderAppBarFilterDrawer(filterDrawerOpen, () =>
            setFilterDrawerOpen(false),
          )
        : null}

      <HelpPanelDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        returnFocusRef={accountMenuTriggerRef}
      />
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        returnFocusRef={accountMenuTriggerRef}
      />
    </Box>
  );
}
